import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp,
  TrendingDown,
  Users,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  BarChart3,
  Instagram,
  Facebook,
  Twitter,
  Linkedin,
  Youtube,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';

interface PlatformMetrics {
  platform: string;
  followers: number;
  followersChange: number;
  engagement: number;
  engagementChange: number;
  reach: number;
  reachChange: number;
  impressions: number;
  impressionsChange: number;
}

interface PostMetrics {
  id: string;
  content: string;
  platform: string;
  published_at: string;
  likes: number;
  comments: number;
  shares: number;
  reach: number;
  engagement_rate: number;
}

interface SocialAccount {
  id: string;
  platform: string;
  username: string;
  followers: number;
  following?: number;
  posts_count?: number;
  connected: boolean;
}

interface SocialPost {
  id: string;
  content: string;
  platforms: string[];
  published_at?: string;
  metrics?: {
    likes: number;
    comments: number;
    shares: number;
    reach: number;
    engagement_rate: number;
  };
}

interface AnalyticsData {
  platformMetrics: Array<{
    platform: string;
    username: string;
    profileImage?: string;
    followers: number;
    following: number;
    postsCount: number;
    followersChange: number;
    engagement: number;
    engagementChange: number;
    reach: number;
    reachChange: number;
    impressions: number;
    impressionsChange: number;
    warning?: string | null;
  }>;
  topPosts: SocialPost[];
  overview: {
    totalFollowers: number;
    avgEngagement: number;
    totalReach: number;
    totalImpressions: number;
  };
}

interface SocialAnalyticsProps {
  loading?: boolean;
  accounts?: SocialAccount[];
  posts?: SocialPost[];
  analyticsData?: {
    platformMetrics?: any[];
    topPosts?: any[];
    overview?: any;
  } | null;
}

const platformIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  instagram: Instagram,
  facebook: Facebook,
  twitter: Twitter,
  linkedin: Linkedin,
  youtube: Youtube,
  youtube_shorts: Youtube,
  tiktok: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
  ),
  threads: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.332-3.023.88-.73 2.106-1.146 3.451-1.175 1.016-.022 1.96.139 2.818.468-.073-.938-.306-1.683-.7-2.216-.453-.614-1.143-.936-2.053-.958l-.023-.003c-.689 0-1.58.142-2.433.907l-1.416-1.486c1.249-1.12 2.664-1.538 4.001-1.538h.036c1.478.034 2.689.548 3.6 1.528.857.922 1.366 2.2 1.514 3.8.475.19.917.422 1.32.695 1.198.813 2.074 1.922 2.533 3.205.752 2.106.593 4.932-1.635 7.11C18.581 23.087 15.994 23.973 12.186 24z"/>
    </svg>
  )
};

const defaultColors = { bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-600 dark:text-gray-400', gradient: 'from-gray-500 to-gray-600' };

const platformColors: Record<string, { bg: string; text: string; gradient: string }> = {
  instagram: { bg: 'bg-pink-100 dark:bg-pink-900/30', text: 'text-pink-600 dark:text-pink-400', gradient: 'from-purple-500 to-pink-500' },
  facebook: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400', gradient: 'from-blue-600 to-blue-500' },
  twitter: { bg: 'bg-sky-100 dark:bg-sky-900/30', text: 'text-sky-600 dark:text-sky-400', gradient: 'from-sky-400 to-sky-500' },
  linkedin: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', gradient: 'from-blue-700 to-blue-600' },
  youtube: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400', gradient: 'from-red-600 to-red-500' },
  youtube_shorts: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400', gradient: 'from-red-500 to-orange-500' },
  tiktok: { bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-900 dark:text-gray-100', gradient: 'from-gray-900 to-pink-500' },
  threads: { bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-900 dark:text-gray-100', gradient: 'from-gray-900 to-gray-600' }
};

const SocialAnalytics: React.FC<SocialAnalyticsProps> = ({ loading = false, accounts = [], posts = [], analyticsData: initialAnalyticsData }) => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('30d');
  const [analyticsData, setAnalyticsData] = useState<{
    platformMetrics?: any[];
    topPosts?: any[];
    overview?: any;
  } | null>(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);

  // Update local state when prop changes
  React.useEffect(() => {
    if (initialAnalyticsData) {
      setAnalyticsData(initialAnalyticsData);
    }
  }, [initialAnalyticsData]);

  // Fetch analytics when period changes
  React.useEffect(() => {
    const fetchAnalytics = async () => {
      setIsLoadingAnalytics(true);
      try {
        const { contentHubService } = await import('../../services/contentHubService');
        const data = await contentHubService.getAnalytics(period);
        setAnalyticsData({
          platformMetrics: data.platformMetrics || [],
          topPosts: data.topPosts || [],
          overview: data.overview || {}
        });
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setIsLoadingAnalytics(false);
      }
    };
    fetchAnalytics();
  }, [period]);

  // Use analytics data from API if available, otherwise calculate from posts
  const platformMetrics: PlatformMetrics[] = analyticsData?.platformMetrics
    ? analyticsData.platformMetrics.map(pm => ({
        platform: pm.platform,
        followers: pm.followers,
        followersChange: pm.followersChange || 0,
        engagement: pm.engagement || 0,
        engagementChange: pm.engagementChange || 0,
        reach: pm.reach || 0,
        reachChange: pm.reachChange || 0,
        impressions: pm.impressions || 0,
        impressionsChange: pm.impressionsChange || 0
      }))
    : accounts.filter(a => a.connected).map(account => {
        const platformPosts = posts.filter(p => p.platforms.includes(account.platform) && p.metrics);
        const totalEngagement = platformPosts.reduce((sum, p) => sum + (p.metrics?.engagement_rate || 0), 0);
        const avgEngagement = platformPosts.length > 0 ? totalEngagement / platformPosts.length : 0;
        const totalReach = platformPosts.reduce((sum, p) => sum + (p.metrics?.reach || 0), 0);
        const totalImpressions = platformPosts.reduce((sum, p) => sum + ((p.metrics?.reach || 0) * 2.5), 0);

        return {
          platform: account.platform,
          followers: account.followers,
          followersChange: 0,
          engagement: Math.round(avgEngagement * 10) / 10,
          engagementChange: 0,
          reach: totalReach,
          reachChange: 0,
          impressions: Math.round(totalImpressions),
          impressionsChange: 0
        };
      });

  // Build top posts from analytics data or fall back to posts prop
  const topPosts: PostMetrics[] = (analyticsData?.topPosts || posts)
    .filter((p: any) => p.metrics && (p.published_at || p.scheduledFor))
    .sort((a: any, b: any) => (b.metrics?.engagement_rate || 0) - (a.metrics?.engagement_rate || 0))
    .slice(0, 5)
    .map((post: any) => ({
      id: post.id,
      content: typeof post.content === 'string' ? post.content : (post.content?.text || ''),
      platform: (post.platforms?.[0] || 'instagram'),
      published_at: post.published_at || post.scheduledFor || new Date().toISOString(),
      likes: post.metrics?.likes || 0,
      comments: post.metrics?.comments || 0,
      shares: post.metrics?.shares || 0,
      reach: post.metrics?.reach || 0,
      engagement_rate: post.metrics?.engagement_rate || 0
    }));

  // Use overview data from API if available
  const totalFollowers = analyticsData?.overview?.totalFollowers ?? platformMetrics.reduce((sum, p) => sum + p.followers, 0);
  const avgFollowersChange = platformMetrics.length > 0 ? platformMetrics.reduce((sum, p) => sum + p.followersChange, 0) / platformMetrics.length : 0;
  const avgEngagement = analyticsData?.overview?.avgEngagement ?? (platformMetrics.length > 0 ? platformMetrics.reduce((sum, p) => sum + p.engagement, 0) / platformMetrics.length : 0);
  const totalReach = analyticsData?.overview?.totalReach ?? platformMetrics.reduce((sum, p) => sum + p.reach, 0);
  const totalImpressions = analyticsData?.overview?.totalImpressions ?? platformMetrics.reduce((sum, p) => sum + p.impressions, 0);

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString(undefined, {
      day: '2-digit',
      month: 'short'
    });
  };

  const isLoading = loading || isLoadingAnalytics;

  if (loading && !analyticsData) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
          ))}
        </div>
        <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 dark:text-white">
          {t('social.analytics', 'Análise de Performance')}
        </h3>
        <div className="flex items-center gap-2">
          {isLoadingAnalytics && (
            <div className="animate-spin h-4 w-4 border-2 border-primary-500 border-t-transparent rounded-full" />
          )}
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          {(['7d', '30d', '90d'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                period === p
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              {p === '7d' ? t('common.days7', '7 days') : p === '30d' ? t('common.days30', '30 days') : t('common.days90', '90 days')}
            </button>
          ))}
          </div>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('social.totalFollowers', 'Total de Seguidores')}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {formatNumber(totalFollowers)}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-primary-100 dark:bg-primary-900/30">
                <Users className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
            </div>
            {/* Only show the trend when there is a real, non-zero change */}
            {avgFollowersChange !== 0 && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${avgFollowersChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {avgFollowersChange >= 0 ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
                <span>{Math.abs(avgFollowersChange).toFixed(1)}%</span>
                <span className="text-gray-500 dark:text-gray-400">{t('common.vsLastPeriod', 'vs período anterior')}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('social.avgEngagement', 'Engajamento Médio')}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {avgEngagement.toFixed(1)}%
                </p>
              </div>
              <div className="p-3 rounded-xl bg-pink-100 dark:bg-pink-900/30">
                <Heart className="h-6 w-6 text-pink-600 dark:text-pink-400" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-2 text-sm text-gray-500 dark:text-gray-400">
              <span>{t('common.noHistoricalData', 'Sem dados históricos')}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('social.totalReach', 'Alcance Total')}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {formatNumber(totalReach)}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-900/30">
                <Eye className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-2 text-sm text-gray-500 dark:text-gray-400">
              <span>{t('common.noHistoricalData', 'Sem dados históricos')}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('social.impressions', 'Impressões')}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {formatNumber(totalImpressions)}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-purple-100 dark:bg-purple-900/30">
                <BarChart3 className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-2 text-sm text-gray-500 dark:text-gray-400">
              <span>{t('common.noHistoricalData', 'Sem dados históricos')}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Platform Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>{t('social.platformBreakdown', 'Por Plataforma')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {platformMetrics.map((metrics) => {
              const Icon = platformIcons[metrics.platform];
              const colors = platformColors[metrics.platform] || defaultColors;

              return (
                <div
                  key={metrics.platform}
                  className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors"
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${colors.gradient} text-white`}>
                    {Icon && <Icon className="h-6 w-6" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white capitalize">
                      {metrics.platform}
                    </p>
                    {/* LinkedIn API doesn't provide follower metrics */}
                    {metrics.platform !== 'linkedin' ? (
                      <div className="flex flex-col gap-1 mt-1">
                        <div className="flex items-center gap-4">
                          {metrics.followers > 0 && (
                            <>
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                {formatNumber(metrics.followers)} {t('social.followers', 'Followers')}
                              </span>
                              {metrics.followersChange !== 0 && (
                                <span className={`text-sm flex items-center gap-0.5 ${metrics.followersChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {metrics.followersChange >= 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                                  {Math.abs(metrics.followersChange).toFixed(1)}%
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        {(metrics as any).warning && (
                          <span className="text-xs text-amber-600 dark:text-amber-400">
                            ⚠️ {metrics.platform === 'threads'
                              ? t('social.threadsWarning', 'Requer 100+ seguidores para insights completos')
                              : (metrics as any).warning}
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 mt-1">{t('social.metricsNotAvailable', 'Métricas não disponíveis via API')}</p>
                    )}
                  </div>
                  {/* LinkedIn API doesn't provide these metrics */}
                  {metrics.platform !== 'linkedin' ? (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 text-center">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('social.engagement', 'Engagement')}</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{metrics.engagement}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('social.reach', 'Reach')}</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{formatNumber(metrics.reach)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('social.impressions', 'Impressions')}</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{formatNumber(metrics.impressions)}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <p className="text-sm text-gray-400">{t('social.apiRestricted', 'Restricted API')}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Top Performing Posts */}
      <Card>
        <CardHeader>
          <CardTitle>{t('social.topPosts', 'Melhores Posts')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {topPosts.map((post) => {
              const Icon = platformIcons[post.platform];
              const colors = platformColors[post.platform] || defaultColors;

              return (
                <div
                  key={post.id}
                  className="flex items-start gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700 transition-colors cursor-pointer"
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br ${colors.gradient} text-white flex-shrink-0`}>
                    {Icon && <Icon className="h-5 w-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 dark:text-white line-clamp-2">
                      {post.content}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {formatDate(post.published_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <Heart className="h-4 w-4 text-pink-500" />
                      {post.likes}
                    </span>
                    <span className="flex items-center gap-1">
                      <MessageCircle className="h-4 w-4 text-blue-500" />
                      {post.comments}
                    </span>
                    <span className="flex items-center gap-1">
                      <Share2 className="h-4 w-4 text-green-500" />
                      {post.shares}
                    </span>
                    <span className="font-medium text-primary-600 dark:text-primary-400">
                      {post.engagement_rate}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SocialAnalytics;
